import { FC, useMemo } from 'react';
import { ModifyDatesOccupancyFormData, ModifyDatesOccupancyModalProps } from './ModifyDatesOccupancyModal.types';
import { StyledModifyDatesOccupancyModal } from './ModifyDatesOccupancyModal.styles';
import { Button, Modal, baseVariables } from '@marriott/mi-ui-library';
import { DatePickerDesktop, DatePickerMobileContainer, Dropdown } from '../../../../../../molecules';
import { Controller, useForm } from 'react-hook-form';
import { useMediaQuery } from '../../../../../../hooks';
import { getFormattedCalendarDate, getFormattedDateObject } from '../../../../../../utils';

export const ModifyDatesOccupancyModal: FC<ModifyDatesOccupancyModalProps> = ({
  show,
  labels,
  defaultValues,
  closeModal,
  onModify,
}) => {
  const {
    heading,
    modifyArrivalInfo,
    maxGuestPerRoomInfo,
    miniHotelInfo,
    combineInfo,
    maxGuestsRoomLabel,
    maxGuestsRoomValue,
    confirm,
    cancel,
    dateLabels,
  } = labels;
  const { id, startDate, endDate, overriddenStartDate, overriddenEndDate, maxGuestsPerRoom } = defaultValues;

  const isDesktop = useMediaQuery(baseVariables.mediaQuery.lg);

  const maxGuestRoomOptions = useMemo(
    () =>
      Array.from({ length: parseInt(maxGuestsRoomValue) }, (_, index) => ({
        label: `${index + 1}`,
        value: `${index + 1}`,
      })),
    [maxGuestsRoomValue]
  );

  const { control, getValues, setValue } = useForm<ModifyDatesOccupancyFormData>({
    defaultValues: {
      overriddenStartDate,
      overriddenEndDate,
      maxGuestsPerRoom: { label: '', value: '' },
    },
  });

  const handleDatesChange = (dates: string, fromDate: string, toDate: string) => {
    const formattedFromDate = fromDate && getFormattedCalendarDate(fromDate, 'hyphenatedDateWithMonthNoAndYear');
    const formattedToDate = toDate && getFormattedCalendarDate(toDate, 'hyphenatedDateWithMonthNoAndYear');
    if (formattedFromDate !== startDate || formattedToDate !== endDate || (overriddenStartDate && overriddenEndDate)) {
      setValue('dates', dates);
      setValue('overriddenStartDate', formattedFromDate);
      setValue('overriddenEndDate', formattedToDate);
    }
  };

  const handleConfirm = () => {
    const formValues = getValues();
    const selectedStartDate = formValues.overriddenStartDate || '';
    const selectedEndDate = formValues.overriddenEndDate || '';
    const selectedMaxGuestsPerRoom = formValues.maxGuestsPerRoom?.value;

    onModify({
      id,
      startDate,
      endDate,
      overriddenStartDate: selectedStartDate,
      overriddenEndDate: selectedEndDate,
      maxGuestsPerRoom: selectedMaxGuestsPerRoom,
    });

    closeModal();
  };

  const datesField = (
    <Controller
      control={control}
      name="dates"
      render={() => (
        <DatePickerDesktop
          dates={dateLabels}
          align={'left'}
          showFlexibleDates={false}
          selectedStartDate={getFormattedDateObject(overriddenStartDate || startDate)}
          selectedEndDate={getFormattedDateObject(overriddenEndDate || endDate)}
          isDateRangeFixed={true}
          rangeStartDate={getFormattedDateObject(startDate)}
          rangeEndDate={getFormattedDateObject(endDate)}
          onChange={handleDatesChange}
        />
      )}
    />
  );

  const mobileDatesField = (
    <Controller
      control={control}
      name="dates"
      render={() => (
        <DatePickerMobileContainer
          dates={dateLabels}
          startDate={getFormattedDateObject(overriddenStartDate || startDate)}
          endDate={getFormattedDateObject(overriddenEndDate || endDate)}
          isDateRangeFixed={true}
          flexibleDates={false}
          rangeStartDate={getFormattedDateObject(startDate)}
          rangeEndDate={getFormattedDateObject(endDate)}
          onChange={handleDatesChange}
        />
      )}
    />
  );

  const maxGuestsPerRoomField = (
    <Controller
      name={'maxGuestsPerRoom'}
      control={control}
      render={() => (
        <Dropdown
          name={'maxGuestsPerRoom'}
          label={maxGuestsRoomLabel}
          ariaLabel={maxGuestsRoomLabel}
          className="dropdown"
          options={maxGuestRoomOptions}
          selectedValue={maxGuestsPerRoom}
          onChange={({ label, value }) => {
            if (value !== maxGuestsPerRoom) {
              setValue('maxGuestsPerRoom.label', label);
              setValue('maxGuestsPerRoom.value', value);
            }
          }}
        />
      )}
    />
  );

  return (
    <StyledModifyDatesOccupancyModal
      modalId="modifyDatesOccupancyModal"
      labelledBy="modifyDatesOccupancyModal"
      role="dialog"
      show={show}
      popupOpenState={show}
      disableScrollOnBody={true}
      secondaryClassName="mt-lg-2"
      handleBlur={!!isDesktop}
      onClose={closeModal}
      setPopupOpenState={closeModal}
    >
      <Modal.Header labelText={heading} popupHeaderOnCLoseFunc={closeModal}></Modal.Header>
      <Modal.Body className="px-5 custom-scrollbar modal-body">
        <div className="row my-3">
          <div className="col-md-6 mb-3 mb-md-0">{isDesktop ? datesField : mobileDatesField}</div>
          <div className="col-md-6">{maxGuestsPerRoomField}</div>
        </div>
        <p>
          <span>{`${modifyArrivalInfo} `}</span>
          <span className="minihotel-info">{miniHotelInfo}</span>
          <br />
          <span className="d-inline-block">{combineInfo}</span>
          <br />
          <span>{`${maxGuestPerRoomInfo} `}</span>
          <span className="minihotel-info">{miniHotelInfo}</span>
        </p>
      </Modal.Body>
      <Modal.FooterGeneric
        fadeEffect={true}
        actions={
          <>
            <Button className={'m-button-secondary'} buttonCopy={cancel} callback={closeModal} />
            <Button className={'m-button-primary'} buttonCopy={confirm} callback={handleConfirm} />
          </>
        }
      ></Modal.FooterGeneric>
    </StyledModifyDatesOccupancyModal>
  );
};
